import React, { useState, useEffect, useRef } from 'react';
import { jobsAPI } from '../utils/api';
import { Calendar as CalendarIcon, Circle, Users, Printer, ClipboardList } from 'lucide-react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, startOfWeek, endOfWeek } from 'date-fns';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import ResourceCalendar from '../components/ResourceCalendar';
import EstimatorCalendar from '../components/EstimatorCalendar';

const Schedule = () => {
  const [jobs, setJobs] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('upcoming'); // 'upcoming' or 'completed'
  const [scheduleView, setScheduleView] = useState('calendar'); // 'calendar', 'resources', or 'estimators'
  const printRef = useRef();

  useEffect(() => {
    loadJobs();
  }, []);

  const loadJobs = async () => {
    try {
      const response = await jobsAPI.getAll();
      // Filter jobs that have either scheduled_date or estimated_completion_date
      setJobs(response.data.filter((job) => job.scheduled_date || job.estimated_completion_date));
    } catch (error) {
      toast.error('Failed to load schedule');
    } finally {
      setLoading(false);
    }
  };

  // Filter jobs based on toggle
  const filteredJobs = jobs.filter(job => {
    if (viewMode === 'upcoming') {
      return job.status !== 'completed';
    } else {
      return job.status === 'completed';
    }
  });

  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const startDate = startOfWeek(monthStart);
  const endDate = endOfWeek(monthEnd);
  const days = eachDayOfInterval({ start: startDate, end: endDate });

  // Get events for a specific day (both start and end dates)
  const getEventsForDay = (day) => {
    const events = [];
    
    filteredJobs.forEach((job) => {
      // Check for start date
      if (job.scheduled_date && isSameDay(new Date(job.scheduled_date), day)) {
        events.push({
          ...job,
          type: 'start',
          label: `${job.job_code || ''} ${job.title}`.trim(),
        });
      }
      
      // Check for end date
      if (job.estimated_completion_date && isSameDay(new Date(job.estimated_completion_date), day)) {
        events.push({
          ...job,
          type: 'end',
          label: `${job.job_code || ''} ${job.title} (End)`.trim(),
        });
      }
    });
    
    return events;
  };

  // Print handler for job schedule
  const handlePrintJobSchedule = () => {
    const printContent = printRef.current;
    if (!printContent) return;
    
    const printWindow = window.open('', '_blank');
    
    printWindow.document.write(`
      <html>
        <head>
          <title>Job Schedule - ${format(currentDate, 'MMMM yyyy')}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: Arial, sans-serif; padding: 20px; }
            .header { text-align: center; margin-bottom: 20px; }
            .header h1 { font-size: 24px; color: #1e3a5f; }
            .header p { color: #666; margin-top: 5px; }
            .calendar-grid { display: grid; grid-template-columns: repeat(7, 1fr); gap: 4px; }
            .day-header { background: #1e3a5f; color: white; padding: 8px; text-align: center; font-weight: bold; }
            .day-cell { border: 1px solid #e5e7eb; min-height: 80px; padding: 4px; }
            .day-number { font-weight: bold; font-size: 14px; margin-bottom: 4px; }
            .event { font-size: 9px; padding: 2px 4px; margin: 2px 0; border-radius: 3px; }
            .event-start { background: #dcfce7; color: #166534; border: 1px solid #bbf7d0; }
            .event-end { background: #ffedd5; color: #9a3412; border: 1px solid #fed7aa; }
            .legend { margin-top: 15px; display: flex; gap: 20px; justify-content: center; }
            .legend-item { display: flex; align-items: center; gap: 5px; font-size: 12px; }
            .legend-dot { width: 10px; height: 10px; border-radius: 50%; }
            @media print { body { padding: 10px; } }
          </style>
        </head>
        <body>
          <div class="header">
            <img src="https://customer-assets.emergentagent.com/job_lbr-build/artifacts/t9f432h8_MB%20Con%20Logo.png" alt="MBConstruction" style="height: 80px; margin-bottom: 10px;" />
            <p>Job Schedule - ${format(currentDate, 'MMMM yyyy')}</p>
          </div>
          <div class="calendar-grid">
            ${['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => `<div class="day-header">${day}</div>`).join('')}
            ${days.map(day => {
              const dayEvents = getEventsForDay(day);
              const isCurrentMonth = day.getMonth() === currentDate.getMonth();
              return `
                <div class="day-cell" style="background: ${isCurrentMonth ? 'white' : '#f8fafc'}">
                  <div class="day-number" style="color: ${isCurrentMonth ? '#1e293b' : '#94a3b8'}">${format(day, 'd')}</div>
                  ${dayEvents.slice(0, 3).map(event => `
                    <div class="event ${event.type === 'start' ? 'event-start' : 'event-end'}">
                      ${event.job_code ? event.job_code + ' - ' : ''}${event.title}${event.type === 'end' ? ' (End)' : ''}
                    </div>
                  `).join('')}
                  ${dayEvents.length > 3 ? `<div style="font-size: 9px; color: #64748b;">+${dayEvents.length - 3} more</div>` : ''}
                </div>
              `;
            }).join('')}
          </div>
          <div class="legend">
            <div class="legend-item"><div class="legend-dot" style="background: #22c55e;"></div> Job Start Date</div>
            <div class="legend-item"><div class="legend-dot" style="background: #f97316;"></div> Job End Date</div>
          </div>
        </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 250);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading schedule...</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="schedule-page" className="p-6">
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Schedule
        </h1>
        <p className="text-slate-600">View and manage job schedules and resource assignments</p>
        
        {/* Main View Toggle: Calendar vs Resource Calendar vs Estimator Calendar */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-4 mt-4">
          <div className="inline-flex rounded-lg border border-slate-200 p-1 bg-slate-50">
            <button
              onClick={() => setScheduleView('calendar')}
              data-testid="view-job-calendar-btn"
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                scheduleView === 'calendar'
                  ? 'bg-white text-blue-700 shadow-sm border border-slate-200'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <CalendarIcon className="w-4 h-4" />
              Job Calendar
            </button>
            <button
              onClick={() => setScheduleView('resources')}
              data-testid="view-resource-calendar-btn"
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                scheduleView === 'resources'
                  ? 'bg-white text-blue-700 shadow-sm border border-slate-200'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <Users className="w-4 h-4" />
              Resource Calendar
            </button>
            <button
              onClick={() => setScheduleView('estimators')}
              data-testid="view-estimator-calendar-btn"
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                scheduleView === 'estimators'
                  ? 'bg-white text-purple-700 shadow-sm border border-slate-200'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <ClipboardList className="w-4 h-4" />
              Estimator Calendar
            </button>
          </div>
        </div>
        
        {/* Show sub-toggles only for Job Calendar view */}
        {scheduleView === 'calendar' && (
          <>
            {/* Toggle for Upcoming vs Completed */}
            <div className="flex items-center gap-4 mt-4">
              <div className="inline-flex rounded-lg border border-slate-200 p-1 bg-slate-50">
                <button
                  onClick={() => setViewMode('upcoming')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    viewMode === 'upcoming'
                      ? 'bg-white text-blue-700 shadow-sm border border-slate-200'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  Upcoming Jobs
                </button>
                <button
                  onClick={() => setViewMode('completed')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    viewMode === 'completed'
                      ? 'bg-white text-green-700 shadow-sm border border-slate-200'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  Completed Jobs
                </button>
              </div>
            </div>
            
            {/* Legend */}
            <div className="flex items-center gap-6 mt-4">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
                <span className="text-sm text-slate-600">Job Start Date</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-orange-500"></div>
                <span className="text-sm text-slate-600">Job End Date</span>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Conditional Rendering based on scheduleView */}
      {scheduleView === 'resources' ? (
        <ResourceCalendar />
      ) : scheduleView === 'estimators' ? (
        <EstimatorCalendar />
      ) : (
        <div ref={printRef}>
          {/* Existing Job Calendar View */}
          <div className="bg-white border border-slate-200 shadow-sm rounded-xl p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {format(currentDate, 'MMMM yyyy')}
              </h2>
              <div className="flex gap-2">
                <button
                  onClick={handlePrintJobSchedule}
                  data-testid="print-job-schedule-btn"
                  className="flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 hover:bg-slate-200 font-medium rounded-lg transition-colors"
                >
                  <Printer className="w-4 h-4" />
                  Print
                </button>
                <button
                  data-testid="prev-month-button"
                  onClick={() => setCurrentDate(new Date(currentDate.setMonth(currentDate.getMonth() - 1)))}
                  className="px-4 py-2 border border-slate-200 text-slate-700 hover:bg-slate-50 font-medium rounded-lg transition-colors"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentDate(new Date())}
                  className="px-4 py-2 bg-blue-50 text-blue-700 hover:bg-blue-100 font-medium rounded-lg transition-colors"
                >
                  Today
                </button>
                <button
                  data-testid="next-month-button"
                  onClick={() => setCurrentDate(new Date(currentDate.setMonth(currentDate.getMonth() + 1)))}
                  className="px-4 py-2 border border-slate-200 text-slate-700 hover:bg-slate-50 font-medium rounded-lg transition-colors"
                >
                  Next
                </button>
              </div>
            </div>

        <div className="grid grid-cols-7 gap-2">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
            <div key={day} className="text-center font-semibold text-slate-700 py-2">
              {day}
            </div>
          ))}
          {days.map((day, idx) => {
            const dayEvents = getEventsForDay(day);
            const isCurrentMonth = day.getMonth() === currentDate.getMonth();
            const isToday = isSameDay(day, new Date());
            return (
              <div
                key={idx}
                data-testid={`calendar-day-${format(day, 'yyyy-MM-dd')}`}
                className={`min-h-28 border rounded-lg p-2 ${
                  isCurrentMonth ? 'bg-white' : 'bg-slate-50'
                } ${isToday ? 'border-blue-500 border-2' : 'border-slate-200'}`}
              >
                <div className={`text-sm font-medium mb-1 ${isToday ? 'text-blue-600' : 'text-slate-900'}`}>
                  {format(day, 'd')}
                </div>
                <div className="space-y-1">
                  {dayEvents.slice(0, 3).map((event, eventIdx) => (
                    <Link key={`${event.id}-${event.type}-${eventIdx}`} to={`/jobs/${event.id}`}>
                      <div 
                        className={`text-xs p-1.5 rounded truncate transition-colors cursor-pointer ${
                          event.type === 'start' 
                            ? 'bg-green-50 hover:bg-green-100 border border-green-200' 
                            : 'bg-orange-50 hover:bg-orange-100 border border-orange-200'
                        }`}
                      >
                        <span 
                          className={`font-semibold ${
                            event.type === 'start' ? 'text-green-700' : 'text-orange-700'
                          }`}
                          style={{ fontFamily: 'monospace' }}
                        >
                          {event.job_code && `${event.job_code} - `}
                        </span>
                        <span className={event.type === 'start' ? 'text-green-700' : 'text-orange-700'}>
                          {event.title}
                          {event.type === 'end' && ' (End)'}
                        </span>
                      </div>
                    </Link>
                  ))}
                  {dayEvents.length > 3 && (
                    <div className="text-xs text-slate-500 font-medium">+{dayEvents.length - 3} more</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Jobs List */}
      <div className="mt-6 bg-white border border-slate-200 shadow-sm rounded-xl p-6">
        <h3 className="text-lg font-semibold text-slate-900 mb-4">
          {viewMode === 'upcoming' ? 'Upcoming Jobs' : 'Completed Jobs'}
        </h3>
        <div className="space-y-3">
          {viewMode === 'upcoming' ? (
            // Upcoming Jobs
            filteredJobs
              .filter(job => job.scheduled_date && new Date(job.scheduled_date) >= new Date())
              .sort((a, b) => new Date(a.scheduled_date) - new Date(b.scheduled_date))
              .slice(0, 10)
              .map((job) => (
                <Link key={job.id} to={`/jobs/${job.id}`}>
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 rounded-full bg-green-500"></div>
                      <div>
                        <div className="flex items-center gap-2">
                          {job.job_code && (
                            <span className="text-xs font-mono font-bold text-blue-600">
                              {job.job_code}
                            </span>
                          )}
                          <span className="font-medium text-slate-900">{job.title}</span>
                        </div>
                        <span className="text-sm text-slate-600">
                          Starts: {format(new Date(job.scheduled_date), 'MMM dd, yyyy')}
                          {job.estimated_completion_date && (
                            <> → Ends: {format(new Date(job.estimated_completion_date), 'MMM dd, yyyy')}</>
                          )}
                        </span>
                      </div>
                    </div>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      job.status === 'completed' ? 'bg-green-100 text-green-700' :
                      job.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                      'bg-slate-100 text-slate-600'
                    }`}>
                      {job.status.replace('_', ' ')}
                    </span>
                  </div>
                </Link>
              ))
          ) : (
            // Completed Jobs
            filteredJobs
              .sort((a, b) => new Date(b.completed_date || b.updated_at) - new Date(a.completed_date || a.updated_at))
              .slice(0, 10)
              .map((job) => (
                <Link key={job.id} to={`/jobs/${job.id}`}>
                  <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg hover:bg-green-100 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 rounded-full bg-green-600"></div>
                      <div>
                        <div className="flex items-center gap-2">
                          {job.job_code && (
                            <span className="text-xs font-mono font-bold text-blue-600">
                              {job.job_code}
                            </span>
                          )}
                          <span className="font-medium text-slate-900">{job.title}</span>
                        </div>
                        <span className="text-sm text-slate-600">
                          {job.scheduled_date && <>Started: {format(new Date(job.scheduled_date), 'MMM dd, yyyy')}</>}
                          {job.completed_date && <> → Completed: {format(new Date(job.completed_date), 'MMM dd, yyyy')}</>}
                        </span>
                      </div>
                    </div>
                    <span className="px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-700">
                      completed
                    </span>
                  </div>
                </Link>
              ))
          )}
          {filteredJobs.length === 0 && (
            <p className="text-slate-500 text-center py-4">
              {viewMode === 'upcoming' ? 'No upcoming jobs scheduled' : 'No completed jobs found'}
            </p>
          )}
        </div>
      </div>
        </div>
      )}
    </div>
  );
};

export default Schedule;
